/**
 * Users API client + backend → legacy frontend shape normalizer.
 *
 * Many frontend surfaces (Discover, Feed, Search, BarberProfile, Rankings)
 * grew around `generateMockBarbers()` and expect a rich camelCase shape
 * (specialty, rating, wins, losses, points, isVerified, globalRank, etc.).
 * The backend returns a leaner snake_case projection. This adapter centralizes
 * the conversion so we can swap `generateMockBarbers()` calls for real backend
 * fetches without rewriting every page.
 */
import { api } from '@/lib/api';

const DEFAULT_AVATAR = 'https://api.dicebear.com/7.x/initials/svg?seed=';

/**
 * Convert a backend `users` resource into the legacy mock-barber shape.
 * Missing fields default to safe values so existing components don't crash
 * on null/undefined paths. UI calling code must NOT assume mocked combat
 * stats are real — those default to 0 and are only present when the backend
 * starts emitting them.
 */
export const normalizeBackendUser = (u) => {
  if (!u || typeof u !== 'object') return null;
  // Already in legacy shape (has wins/losses/specialty) → pass-through.
  if (u.wins !== undefined && u.losses !== undefined && u.specialty !== undefined) return u;

  return {
    id: u.id,
    username: u.username,
    name: u.name || u.username,
    avatar: u.avatar || `${DEFAULT_AVATAR}${encodeURIComponent(u.username || 'B')}`,
    role: u.role || 'barber',
    isVerified: !!u.verified,
    verified: !!u.verified,
    boosted: !!u.boosted,
    country: u.country || '',
    state: u.state || '',
    city: u.city || '',
    globalRank: u.global_rank ?? null,
    rankingDisplay: u.ranking_display || 'world',
    // Legacy combat stats: 0 until backend emits real values.
    specialty: u.specialty || 'Barber',
    rating: u.rating ?? 0,
    wins: u.wins ?? 0,
    losses: u.losses ?? 0,
    points: u.points ?? 0,
    followers: u.followers ?? 0,
    followersCount: u.followers ?? 0,
    bio: u.bio || '',
    shopName: u.shop_name || '',
    instagram: u.instagram || '',
    tiktok: u.tiktok || '',
    facebook: u.facebook || '',
    youtube: u.youtube || '',
    website: u.website || '',
    createdAt: u.created_at,
  };
};

export const usersApi = {
  /**
   * List users. Defaults to role=barber + order_by=ranking which is what
   * the Discover/Rankings surfaces use.
   */
  async list({ role = 'barber', orderBy = 'ranking', q, limit = 50, offset = 0 } = {}) {
    const qs = new URLSearchParams();
    if (role) qs.set('role', role);
    if (orderBy) qs.set('order_by', orderBy);
    if (q) qs.set('q', q);
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    const data = await api.get(`/users?${qs.toString()}`);
    const items = data?.items || [];
    return items.map(normalizeBackendUser).filter(Boolean);
  },

  /** Public profile by username. */
  async getByUsername(username) {
    const u = await api.get(`/users/${username}`);
    return normalizeBackendUser(u);
  },

  /**
   * Iter 172 — Unified user search (single source of truth).
   * Backend: GET /api/users/search — partial match, case/accent
   * insensitive, strips leading '@', relevance-ordered. Admin viewers
   * can also match by email. Rows carry isFollowing / canMessage.
   */
  async searchMeta(q = '', { role, limit = 20, offset = 0 } = {}) {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (role) qs.set('role', role);
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    const data = await api.get(`/users/search?${qs.toString()}`);
    const items = (data?.items || [])
      .map((u) => ({
        ...normalizeBackendUser(u),
        afterHours: u.after_hours || null,
        afterHoursItems: u.after_hours_items || [],
        email: u.email,
        status: u.status,
        isSelf: !!u.is_self,
        isFollowing: !!u.is_following,
        canMessage: u.can_message !== false,
      }))
      .filter((u) => u && u.username);
    return { items, total: data?.total ?? items.length };
  },

  /** Same as searchMeta but returns just the normalized array. */
  async search(q = '', opts = {}) {
    const { items } = await this.searchMeta(q, opts);
    return items;
  },
};

export default usersApi;
